/**
 * Client-side permissions table — must mirror functions/src/utils/permissions.ts
 * and the inline role checks in firestore.rules. Keep these three in sync.
 *
 * Org wall (multi-tenant isolation): rules and Cloud Functions additionally
 * require the caller's orgId token claim to match the org stamped on the
 * project/member docs. The UI never enforces org — it only avoids dead-end
 * UX (e.g. blocking project creation for accounts with no organization).
 */

export type AccessRole = 'owner' | 'manager' | 'editor' | 'reviewer' | 'viewer';

export type Privilege =
  | 'generate'
  | 'upload'
  | 'download'
  | 'edit'
  | 'approve'
  | 'comment'
  | 'manage_members'
  | 'view_activity'
  | 'issue_documents';

export interface ProjectMemberDoc {
  userId: string;
  email?: string;
  displayName?: string;
  accessRole: AccessRole;
  clinicalRole?: string;
  permissionOverrides?: Partial<Record<Privilege, boolean>>;
  addedBy?: string;
  addedAt?: { toDate?: () => Date } | Date;
  acceptedAt?: { toDate?: () => Date } | Date;
}

const ROLE_DEFAULTS: Record<AccessRole, ReadonlySet<Privilege>> = {
  owner: new Set<Privilege>([
    'generate',
    'upload',
    'download',
    'edit',
    'approve',
    'comment',
    'manage_members',
    'view_activity',
    'issue_documents',
  ]),
  manager: new Set<Privilege>([
    'generate',
    'upload',
    'download',
    'edit',
    'approve',
    'comment',
    'manage_members',
    'view_activity',
    'issue_documents',
  ]),
  editor: new Set<Privilege>(['generate', 'upload', 'download', 'edit', 'comment']),
  reviewer: new Set<Privilege>(['download', 'approve', 'comment']),
  viewer: new Set<Privilege>(['download']),
};

export function roleDefaultGrants(role: AccessRole, privilege: Privilege): boolean {
  return ROLE_DEFAULTS[role]?.has(privilege) ?? false;
}

export function memberHasPrivilege(
  member: ProjectMemberDoc,
  privilege: Privilege
): boolean {
  if (member.accessRole === 'owner') return true;
  const override = member.permissionOverrides?.[privilege];
  if (override === true) return true;
  if (override === false) return false;
  return roleDefaultGrants(member.accessRole, privilege);
}

export const ACCESS_ROLE_LABELS: Record<AccessRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  editor: 'Editor',
  reviewer: 'Reviewer',
  viewer: 'Viewer',
};

export const ACCESS_ROLE_DESCRIPTIONS: Record<AccessRole, string> = {
  owner: 'Full control, including project deletion and ownership transfer.',
  manager: 'Manage members and content. Can invite and remove team members.',
  editor: 'Generate and edit content. Cannot manage members.',
  reviewer: 'Read-only with approval and commenting.',
  viewer: 'Read-only.',
};
